// La page que voit une personne dont MANA détient un dossier, en suivant le lien de sa
// notice. Elle n'appartient pas au cabinet : ni bandeau, ni navigation, ni rien qui
// ressemble à une application dans laquelle on entre. Deux gestes, et c'est tout.
//
// Elle parle la langue de la personne. Une notice envoyée en anglais dont la page d'action
// serait en français ne délivrerait qu'à moitié l'information due : les deux gestes que la
// loi garantit doivent être compréhensibles par celui qui les pose.
import { api, direRefus } from '../api.ts';
import { $, h, peindre } from '../ui.ts';

type Langue = 'fr' | 'en';

interface Etat {
  prenom: string; langue: Langue; informe_le: string | null;
  accusee_le: string | null; opposition_le: string | null;
}

interface Mots {
  bonjour: string; sous: string;
  retireSur: string; retire: string;
  accuseSur: string; accuse: string; accusee: string; accuseBouton: string;
  opposerSur: string; opposer: string; opposerBouton: string;
  autreSur: string; autre: string;
  confirmerTitre: string; confirmerSur: string; confirmerTexte: string;
  annuler: string; confirmerBouton: string;
}

const TEXTES: Record<Langue, Mots> = {
  fr: {
    bonjour: 'Bonjour',
    sous: 'Vos données dans le vivier de consultants de WAKEA.',
    retireSur: 'VOTRE DOSSIER A ÉTÉ RETIRÉ',
    retire: "Il ne figure plus dans notre vivier et n'est plus consultable par nos consultants. "
      + "Son effacement définitif suit sous trente jours. Vous n'avez rien d'autre à faire.",
    accuseSur: 'ACCUSER RÉCEPTION',
    accuse: "Cela nous permet de prouver que vous avez bien reçu la notice d'information, et ne vous "
      + 'engage à rien.',
    accusee: 'Réception enregistrée',
    accuseBouton: "J'ai bien reçu la notice",
    opposerSur: 'VOUS OPPOSER',
    opposer: 'Vous pouvez demander le retrait de votre dossier. Il quitte notre vivier immédiatement '
      + 'et sera effacé définitivement. Nous ne vous recontacterons pas.',
    opposerBouton: 'Retirer mon dossier',
    autreSur: 'POUR TOUTE AUTRE DEMANDE',
    autre: 'Accès à vos données, correction, copie, limitation : écrivez-nous, nous répondons dans le '
      + "mois. L'adresse figure dans le courriel que vous avez reçu.",
    confirmerTitre: 'Retirer votre dossier',
    confirmerSur: 'CE GESTE EST DÉFINITIF',
    confirmerTexte: 'Votre dossier quittera notre vivier immédiatement, et nous ne vous proposerons plus de '
      + 'missions. Si vous nous recontactez plus tard, nous repartirons de zéro.',
    annuler: 'Annuler',
    confirmerBouton: 'Confirmer le retrait',
  },
  en: {
    bonjour: 'Hello',
    sous: "Your data in WAKEA's pool of consultants.",
    retireSur: 'YOUR FILE HAS BEEN REMOVED',
    retire: 'It is no longer in our pool and our consultants can no longer see it. Final erasure '
      + 'follows within thirty days. There is nothing else for you to do.',
    accuseSur: 'ACKNOWLEDGE RECEIPT',
    accuse: 'This lets us show that you did receive the information notice. It commits you to nothing.',
    accusee: 'Receipt recorded',
    accuseBouton: 'I have received the notice',
    opposerSur: 'OBJECT',
    opposer: 'You may ask for your file to be removed. It leaves our pool immediately and will be '
      + 'erased for good. We will not contact you again.',
    opposerBouton: 'Remove my file',
    autreSur: 'FOR ANY OTHER REQUEST',
    autre: 'Access to your data, correction, a copy, restriction: write to us and we answer within one '
      + 'month. The address is in the email you received.',
    confirmerTitre: 'Remove your file',
    confirmerSur: 'THIS CANNOT BE UNDONE',
    confirmerTexte: 'Your file will leave our pool immediately and we will no longer offer you '
      + 'assignments. If you contact us later, we start from nothing.',
    annuler: 'Cancel',
    confirmerBouton: 'Confirm removal',
  },
};

const page = (corps: ReturnType<typeof h>) => h`
  <div class="page" style="max-width:640px;margin:0 auto;padding-top:48px">
    <div class="marque" style="margin-bottom:26px"><b>WAKEA</b></div>
    ${corps}
  </div>`;

export async function ecranNotice(jeton: string): Promise<void> {
  const rendre = (e: Etat) => {
    const m = TEXTES[e.langue] ?? TEXTES.fr;
    const retire = Boolean(e.opposition_le);
    peindre('#app', page(h`
      <h1 class="h1">${m.bonjour} ${e.prenom}</h1>
      <p class="sous" style="margin:6px 0 22px">${m.sous}</p>

      ${retire ? h`<div class="enc ar"><span class="sur">${m.retireSur}</span>
          <p>${m.retire}</p></div>`
        : h`
        <div class="enc st"><span class="sur">${m.accuseSur}</span>
          <p>${m.accuse}</p>
          <div class="rang" style="margin-top:11px">
            ${e.accusee_le
              ? h`<span class="et v">${m.accusee}</span>`
              : h`<button class="bt p" data-n="accuse">${m.accuseBouton}</button>`}</div></div>

        <div class="enc ar" style="margin-top:16px"><span class="sur">${m.opposerSur}</span>
          <p>${m.opposer}</p>
          <div class="rang" style="margin-top:11px">
            <button class="bt" data-n="opposer">${m.opposerBouton}</button></div></div>`}

      <div class="enc gl" style="margin-top:16px"><span class="sur">${m.autreSur}</span>
        <p>${m.autre}</p></div>

      <p class="err" id="err" hidden></p>
      <p class="pt sourd" style="margin-top:26px">WAKEA — Advisory & Operating Partner</p>`));
  };

  const confirmer = (e: Etat) => {
    const m = TEXTES[e.langue] ?? TEXTES.fr;
    peindre('#app', page(h`
      <h1 class="h1">${m.confirmerTitre}</h1>
      <div class="enc ar" style="margin-top:18px"><span class="sur">${m.confirmerSur}</span>
        <p>${m.confirmerTexte}</p></div>
      <div class="rang" style="gap:9px;margin-top:18px">
        <button class="bt" data-n="annuler">${m.annuler}</button>
        <button class="bt c" data-n="opposer!">${m.confirmerBouton}</button></div>
      <p class="err" id="err" hidden></p>`));
  };

  const dire = (message: string) => { const n = $('#err'); if (n) { n.textContent = message; n.hidden = false; } };

  let etat: Etat;
  try {
    etat = await api.get<Etat>(`/api/notice/${encodeURIComponent(jeton)}`);
  } catch (e) {
    // La langue se lit dans le dossier, et il n'y a pas de dossier à lire : on dit la
    // chose dans les deux langues plutôt que de deviner.
    peindre('#app', page(h`
      <h1 class="h1">Ce lien n'est plus valable</h1>
      <p class="sous" style="margin-top:8px">${direRefus(e)}</p>
      <div class="enc gl" style="margin-top:18px"><p>Écrivez-nous à l'adresse figurant dans le courriel
        que vous avez reçu : nous traiterons votre demande directement.</p>
        <p style="margin-top:9px">This link is no longer valid. Write to us at the address in the email
        you received and we will handle your request directly.</p></div>`));
    return;
  }
  rendre(etat);

  document.addEventListener('click', async (ev) => {
    const cible = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-n]');
    const geste = cible?.dataset['n'];
    if (!geste) return;
    try {
      if (geste === 'accuse') { etat = { ...etat, ...await api.post<Etat>(`/api/notice/${encodeURIComponent(jeton)}/accuse`) }; rendre(etat); }
      if (geste === 'opposer') confirmer(etat);
      if (geste === 'annuler') rendre(etat);
      if (geste === 'opposer!') { etat = { ...etat, ...await api.post<Etat>(`/api/notice/${encodeURIComponent(jeton)}/opposition`) }; rendre(etat); }
    } catch (e) { dire(direRefus(e)); }
  });
}
